<script lang="ts">
  import type { AccessCode } from '@gat-ship/types'
  import { computed, watch } from 'vue'
  import { useAccessRolePermissionGroup } from './access-role-permission-group-context'
  import { cn } from '@gat-ship/vue-components'
  import type { HTMLAttributes } from 'vue'
  import { useAccessRoleDetailProvider } from '../providers/access-role-detail-context'

  export type PermissionItemProps = {
    /** The title of the permission item. */
    title?: string
    /** The class of the permission item. */
    class?: HTMLAttributes['class']
    /** The access codes of the permission item. Usually just a single code. */
    accessCode: AccessCode
  }
</script>

<script setup lang="ts">
  import { usePermissionsView } from '../../composables/usePermissionsView'
  import { usePermissionDecisions } from '../../composables/usePermissionDecisions'
  import { accessCodeMetadata } from '../../access-control-text'
  import { TooltipHint, Switch } from '@gat-ship/vue-components'
  import { Badge } from '@gat-ship/vue-components'
  import { IconLoading } from '@gat-ship/icons'

  const props = defineProps<PermissionItemProps>()

  const accessRoleProvider = useAccessRoleDetailProvider()

  const { search, isDelayedLoading, isLoading, localStorageState } =
    usePermissionsView()

  const permissionGroupContext = useAccessRolePermissionGroup()

  const decisions = usePermissionDecisions()

  const _title = computed(() => {
    return props.title ?? accessCodeMetadata[props.accessCode].title
  })

  const _privilegeLevel = computed(() => {
    return accessCodeMetadata[props.accessCode].privilegeLevel
  })

  const permissionCodes = computed(() => {
    if (accessRoleProvider.dataSource.accessRole.value) {
      return accessRoleProvider.dataSource.accessRole.value?.permissionCodes
    }
    return []
  })

  const addedWhenPermissionsAreGranted = computed(() => {
    return accessCodeMetadata[props.accessCode].addedWhenPermissionsAreGranted
  })

  const removedWhenPermissionIsRemoved = computed(() => {
    return accessCodeMetadata[props.accessCode].removedWhenPermissionIsRemoved
  })

  const isGranted = computed(() => {
    if (isLoading.value) {
      return false
    }
    return decisions.isGranted(props.accessCode, permissionCodes.value)
  })

  function onUpdatePermission(value: boolean) {
    if (value) {
      accessRoleProvider.dataSource.addRolePermissions(
        addedWhenPermissionsAreGranted.value
      )
    } else {
      accessRoleProvider.dataSource.removeRolePermissions(
        removedWhenPermissionIsRemoved.value
      )
    }
  }

  function addToOrRemoveFromSearchResult(searchValue: string) {
    const matches = decisions.matchesSearch(
      props.accessCode,
      { title: _title.value, groupName: permissionGroupContext.groupName },
      searchValue
    )
    if (matches) {
      permissionGroupContext.addPermissionItemToSearchResult(_title.value)
    } else {
      permissionGroupContext.removePermissionItemFromSearchResult(_title.value)
    }
  }

  watch(
    () => search.value,
    (newValue) => {
      addToOrRemoveFromSearchResult(newValue)
    }
  )

  function isDisabled() {
    if (isLoading.value) {
      return true
    }
    return decisions.isDisabled(props.accessCode, permissionCodes.value)
  }

  function permissionItemShouldBeVisible(searchValue: string | undefined) {
    return decisions.itemIsVisible(props.accessCode, permissionCodes.value, {
      searchValue,
      hideRequiredPermissions: localStorageState.hideRequiredPermissions.value,
      title: _title.value,
      groupName: permissionGroupContext.groupName
    })
  }

  const _description = computed(() => {
    return accessCodeMetadata[props.accessCode].description
  })

  const rolesRequiredTextAndTooltip = computed(() =>
    decisions.rolesRequiredText(props.accessCode, permissionCodes.value)
  )
</script>

<template>
  <div
    v-if="permissionItemShouldBeVisible(search)"
    :class="cn('flex min-h-16 grow items-center py-4', props.class)"
    @click.stop
  >
    <div class="flex grow items-center justify-center gap-2">
      <div class="flex grow flex-row gap-1">
        <div class="mb-px flex grow flex-col gap-1 sm:flex-row sm:items-end">
          <div
            class="text-[13px] font-medium leading-none text-neutral-800 dark:text-neutral-300"
          >
            <div
              class="inline-flex w-fit min-w-0 grow-0 items-center gap-1 sm:items-end"
            >
              {{ _title
              }}<TooltipHint v-if="_description" :text="_description" />
            </div>
          </div>
          <TooltipHint
            v-if="rolesRequiredTextAndTooltip"
            :disabled="rolesRequiredTextAndTooltip.tooltipText === undefined"
          >
            <span class="text-muted-foreground text-xs leading-none sm:hidden">
              {{ rolesRequiredTextAndTooltip.displayText }}
            </span>
            <template #content>
              {{ rolesRequiredTextAndTooltip.tooltipText }}
            </template>
          </TooltipHint>
        </div>
      </div>
      <div class="wrap flex flex-row gap-1">
        <badge
          v-if="_privilegeLevel === 'high' && !isDisabled()"
          class="bg-yellow-200 text-yellow-700 dark:bg-yellow-400/15 dark:text-yellow-400"
        >
          High privilege
        </badge>
      </div>
      <slot
        name="input"
        :model-value="isGranted"
        :on-update-permission="onUpdatePermission"
      >
        <TooltipHint
          v-if="rolesRequiredTextAndTooltip"
          :disabled="rolesRequiredTextAndTooltip.tooltipText === undefined"
        >
          <span
            class="text-muted-foreground hidden text-xs leading-none sm:block"
          >
            {{ rolesRequiredTextAndTooltip.displayText }}
          </span>
          <template #content>
            <div class="whitespace-pre-line">
              {{ rolesRequiredTextAndTooltip.tooltipText }}
            </div>
          </template>
        </TooltipHint>
        <template v-else>
          <IconLoading
            v-show="isDelayedLoading"
            class="text-muted-foreground size-4"
          />
          <Switch
            v-show="!isDelayedLoading"
            :model-value="isGranted"
            :disabled="isDisabled()"
            class="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-neutral-300 dark:data-[state=checked]:bg-green-600 dark:data-[state=unchecked]:bg-neutral-700"
            @keydown.enter.stop="onUpdatePermission(isGranted)"
            @keydown.space.prevent.stop="onUpdatePermission(isGranted)"
            @update:model-value="onUpdatePermission"
          />
        </template>
      </slot>
    </div>
  </div>
  <div v-else class="contents"></div>
</template>
